import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import type { IMatch } from '@/interface/IMatch';
import { deepCompareComputed } from '@/libraries/composables/deepCompareComputed';
import { eventResultScoreBetTypeGroupIds } from '@/libraries/oddsApi/betBuilderScoreSource';
import { formatEvent } from '@/libraries/oddsApi/oddsApiFormatter';
import { useRootStore } from '@/stores/rootStore';
import { EnumOddsCategoryType, useEventQuery as _useEventQuery, useEventSubscription } from '@/types';

export function useEventQuery(
    eventId: MaybeRef<number>,
    isMixParlayMode: MaybeRef<boolean>,
    isEnabled: MaybeRef<boolean>,
    pollInterval: MaybeRef<number> | undefined = undefined,
) {
    const event = ref<IMatch | null>(null);

    const variable = deepCompareComputed(() => ({
        query: {
            id: get(eventId),
            oddsCategory: get(isMixParlayMode) ? EnumOddsCategoryType.MixParlay : EnumOddsCategoryType.All,
        },
        betTypeGroupIds: eventResultScoreBetTypeGroupIds,
    }));

    const { isWsConnected } = storeToRefs(useRootStore());

    const option = deepCompareComputed(() => ({
        enabled: get(isEnabled),
        pollInterval: isWsConnected.value ? undefined : get(pollInterval),
    }));

    const { onResult, refetch, loaded, error } = _useEventQuery(
        variable,
        option,
    );

    onResult((result) => {
        if (!result) {
            return;
        }

        event.value = formatEvent(result);
    });

    const { onSubscription } = useEventSubscription(
        variable,
        deepCompareComputed(() => ({ enabled: get(isEnabled) })),
    );

    onSubscription((result) => {
        if (!result) {
            if (event.value) {
                event.value = { ...event.value, isClosed: true };
            }
        } else {
            event.value = formatEvent(result);
        }
    });

    return {
        event,
        loaded,
        refetch,
        error,
    };
}
